import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Trash2, X } from 'lucide-react'
import { useDeleteOneOnOneSession } from '@/hooks/api-hooks'
import { OneOnOneSessionWithUsers } from '@/types'
import { toast } from 'react-hot-toast'

interface DeleteOneOnOneSessionModalProps {
  isOpen: boolean
  onClose: () => void
  teamId: string
  session: (OneOnOneSessionWithUsers | null)
  onSuccess?: () => void
}

export function DeleteOneOnOneSessionModal({ isOpen, onClose, teamId, session, onSuccess }: DeleteOneOnOneSessionModalProps) {
  const deleteSessionMutation = useDeleteOneOnOneSession()

  const handleConfirmDelete = () => {
    if (!session) return

    deleteSessionMutation.mutate(
      { teamId, sessionId: session.id },
      {
        onSuccess: () => {
          toast.success('1-on-1 session deleted')
          onSuccess?.()
          onClose()
        },
        onError: (error: any) => {
          console.error('Failed to delete 1-on-1 session:', error)
          toast.error(error?.response?.data?.error || error?.message || 'Failed to delete session. Please try again.')
        },
      },
    )
  }

  if (!session) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            Delete 1-on-1 Session
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this 1-on-1 session? This action cannot be undone.
          </p>

          <div className="bg-muted/50 rounded-lg border border-border p-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Session</span>
              <span className="font-medium">{session.title}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Participants</span>
              <span className="font-medium">{session.employee.name} & {session.manager.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Scheduled</span>
              <span className="font-medium">{new Date(session.scheduledDate).toLocaleString()}</span>
            </div>
            {session.status === 'completed' && session.completedAt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Completed</span>
                <span className="font-medium text-green-600">{new Date(session.completedAt).toLocaleString()}</span>
              </div>
            )}
            {session.status !== 'completed' && session.dueDate && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Due</span>
                <span className="font-medium text-amber-600">{new Date(session.dueDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <div className="font-medium">Warning</div>
              <p>Deleting this session will remove all associated notes and ratings. This cannot be undone.</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={deleteSessionMutation.isPending} className="gap-2">
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteSessionMutation.isPending}
              className="gap-2"
            >
              {deleteSessionMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-background"></div>
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Session
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
